// src/services/stepUpPromptBroker.ts — the queue between a refused request and the
// one prompt that answers it (ENG-1378).
//
// A request from a screen with no code field is refused with "set up your
// authenticator first" (403 ADMIN_TWO_FACTOR_SETUP_REQUIRED / 400
// TWO_FACTOR_NOT_ENABLED) or "enter your code" (403 TWO_FACTOR_REQUIRED). The
// shared interceptor and moneyOpRequest hand such a request here instead of
// rejecting it; OperatorTwoFactorPromptHost (mounted once per shell) shows ONE
// prompt, collects the enrolment and a code, and this module re-sends the queued
// requests in order, one per code (the api spends each code once). Each caller's
// promise settles with its re-send's answer, so the screen continues as if the
// first attempt had been answered: success, a dual-control 409, or a refusal.
//
// Framework-free on purpose: the interceptor is module-level axios code, not React.
import type { StepUpHandling } from "./stepUp";
import {
  isOperatorTwoFactorSetupRequired,
  isStepUpCodeAlreadyUsed,
  isStepUpCodeInvalid,
  isStepUpTooManyAttempts,
  STEP_UP_HEADER,
} from "./stepUp";
import {
  StepUpPromptReportedError,
  TwoFactorCancelledError,
} from "./twoFactorErrors";
import { noteOperatorSetupRequired } from "./operatorTwoFactorSignal";

declare module "axios" {
  // Our own field on the request config. See StepUpHandling for the contract.
  interface AxiosRequestConfig {
    stepUp?: StepUpHandling;
    /** What the operator was doing, for the prompt's copy ("Approve payout"). */
    stepUpAction?: string;
  }
}

/** Why the server refused: no factor at all, or a factor whose code is missing. */
export type StepUpChallengeKind = "setup-required" | "code-required";

export interface StepUpChallenge {
  readonly id: number;
  readonly kind: StepUpChallengeKind;
  /** What the operator was doing, for the prompt's copy ("Approve payout"). */
  readonly action?: string;
}

interface QueuedChallenge extends StepUpChallenge {
  resend: (code: string) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

/**
 * What one code came to. One code sends ONE queued request: the api accepts each
 * TOTP code once per operator, so the next request in the queue waits for the
 * next code the app shows (still in the same prompt, never a second one).
 */
export type StepUpResendOutcome =
  /** The queue is empty: every caller has its answer. */
  | { kind: "done" }
  /** The head was answered; `remaining` requests wait for the next code. */
  | { kind: "next-code"; remaining: number }
  /** The code was refused; the queue is intact and wants a fresh code. */
  | { kind: "wrong-code" }
  /** The code was right but already spent; the queue wants the app's next one. */
  | { kind: "code-used" }
  /** The server says the operator has no factor after all; the queue waits for set-up. */
  | { kind: "setup-required" }
  /** The server locked the operator's codes; every caller was rejected. */
  | { kind: "too-many-attempts" }
  /** A re-send got no answer; the outcome is unknown and every caller was rejected. */
  | { kind: "network" };

type Listener = (queue: readonly StepUpChallenge[]) => void;

const ENROLMENT_PATH = "/settings/security/2fa/";

let nextId = 1;
let queue: QueuedChallenge[] = [];
const listeners = new Set<Listener>();
// Hosts in mount order. Only the most recently mounted one renders, so a console
// page that happens to be wrapped in both shells never shows two prompts.
const hosts: symbol[] = [];
// The request whose re-send is on the wire, if any. Closing the prompt cannot
// call it back: its outcome is unknown, not "never sent".
let inFlight: QueuedChallenge | null = null;

const publish = () => {
  const snapshot: readonly StepUpChallenge[] = queue.map(
    ({ id, kind, action }) => ({ id, kind, action })
  );
  listeners.forEach((listener) => listener(snapshot));
};

/** A host is mounted to answer a hand-off (the console shell or the portal layout). */
export const hasStepUpHost = (): boolean => hosts.length > 0;

/**
 * A request may be handed to the host only when it opted in, is not the host's
 * own re-send, is not an enrolment call (set-up must never loop) and a host is
 * mounted to answer it. Otherwise it is rejected exactly as before.
 */
export const canHandOffToStepUpHost = (
  stepUp: StepUpHandling | undefined,
  url: string | undefined
): boolean =>
  stepUp === "host" &&
  hasStepUpHost() &&
  !(typeof url === "string" && url.includes(ENROLMENT_PATH));

/**
 * Queue a refused request behind the one prompt. Resolves with the re-send's
 * answer, or rejects with TwoFactorCancelledError when the operator declines,
 * or with the re-send's own error.
 */
export function challengeStepUp<T>(input: {
  kind: StepUpChallengeKind;
  action?: string;
  resend: (code: string) => Promise<T>;
}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    queue.push({
      id: nextId++,
      kind: input.kind,
      action: input.action,
      resend: input.resend,
      resolve: (value) => resolve(value as T),
      reject,
    });
    publish();
  });
}

/** The host's subscription; returns the unsubscribe. */
export function subscribeStepUpQueue(listener: Listener): () => void {
  listeners.add(listener);
  listener(queue.map(({ id, kind, action }) => ({ id, kind, action })));
  return () => {
    listeners.delete(listener);
  };
}

const rejectAll = (reason: (challenge: QueuedChallenge) => unknown) => {
  const rejected = queue;
  queue = [];
  rejected.forEach((challenge) => challenge.reject(reason(challenge)));
  publish();
};

/**
 * A lockout or a lost answer settles the whole queue, but only the head was
 * re-sent: it carries the server's answer (StepUpPromptReportedError). The rest
 * never left the browser, so they are plain cancellations, and no screen
 * treats them as a write that may have landed.
 */
const reportHeadOnly =
  (head: QueuedChallenge, answer: unknown) =>
  (challenge: QueuedChallenge): TwoFactorCancelledError =>
    challenge === head
      ? new StepUpPromptReportedError(answer)
      : new TwoFactorCancelledError();

/** What an in-flight re-send's caller is told when the prompt closes under it. */
const RESEND_UNCONFIRMED = () =>
  new Error("The prompt closed before the re-send was answered");

/**
 * "Not now", or the prompt closed: nothing more is sent, screens treat it as
 * silent. A re-send already on the wire may still land, so its caller gets the
 * unknown-outcome answer (and re-reads what it acted on), not a cancellation.
 */
export function cancelStepUpQueue(): void {
  rejectAll((challenge) =>
    challenge === inFlight
      ? new StepUpPromptReportedError(RESEND_UNCONFIRMED())
      : new TwoFactorCancelledError()
  );
}

/**
 * Register a mounted host; returns its unregister. Both re-announce the queue, so
 * the host that is now on top renders a prompt that is already open. When the
 * last host goes (the shell unmounted), nothing is left to answer the queue:
 * every caller is cancelled rather than left waiting for a prompt that may
 * reopen later over a different screen.
 */
export function registerStepUpHost(token: symbol): () => void {
  hosts.push(token);
  publish();
  return () => {
    const at = hosts.indexOf(token);
    if (at >= 0) hosts.splice(at, 1);
    if (hosts.length === 0 && queue.length > 0) cancelStepUpQueue();
    else publish();
  };
}

export const isActiveStepUpHost = (token: symbol): boolean =>
  hosts.length > 0 && hosts[hosts.length - 1] === token;

// No answer at all: a dropped connection or a timeout, as the shared client
// (raw axios codes) and moneyOpRequest (its own ApiError codes) name them. A
// timeout is the same unknown outcome as a dropped connection — the request may
// have landed — so both get the "couldn't confirm" answer. Anything else, an
// answered refusal or a plain Error included, is the caller's own answer.
const NO_ANSWER_CODES: readonly string[] = [
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "NETWORK_ERROR",
  "REQUEST_TIMEOUT",
];

const isNetworkFailure = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, code } = error as { status?: unknown; code?: unknown };
  return (
    status === undefined &&
    typeof code === "string" &&
    NO_ANSWER_CODES.includes(code)
  );
};

/** What is left after the head was answered, one way or the other. */
const afterHead = (): StepUpResendOutcome =>
  queue.length > 0
    ? { kind: "next-code", remaining: queue.length }
    : { kind: "done" };

/**
 * Re-send the request at the head of the queue with this code. One code answers
 * one request: the api spends each code once, so the rest of the queue waits for
 * the next code from the app, in the same prompt. A refused or spent code keeps
 * the queue for a fresh one; a lockout or a lost answer clears it, because
 * neither can be fixed by typing another code now (the prompt has already told
 * the operator, so callers get a silent StepUpPromptReportedError).
 */
export async function resendStepUpQueue(
  code: string
): Promise<StepUpResendOutcome> {
  const head = queue[0];
  if (!head) return { kind: "done" };
  inFlight = head;
  // The prompt can close while this re-send is on the wire (its caller is then
  // settled already) and newer requests can queue behind a new prompt. A late
  // answer must not touch that newer state.
  const closedMeanwhile = () => !queue.includes(head);
  try {
    let value: unknown;
    try {
      value = await head.resend(code);
    } finally {
      if (inFlight === head) inFlight = null;
    }
    if (closedMeanwhile()) return { kind: "done" };
    queue = queue.filter((challenge) => challenge !== head);
    head.resolve(value);
    publish();
    return afterHead();
  } catch (error) {
    if (closedMeanwhile()) return { kind: "done" };
    if (isStepUpCodeAlreadyUsed(error)) return { kind: "code-used" };
    if (isStepUpCodeInvalid(error)) return { kind: "wrong-code" };
    if (isOperatorTwoFactorSetupRequired(error)) {
      // requireTwoFactor asks an operator with no factor for a code first and
      // only says "not enabled" once one is sent. Nothing ran: the queue waits
      // for set-up instead of failing the caller.
      noteOperatorSetupRequired();
      queue = queue.map((challenge) =>
        challenge === head ? { ...head, kind: "setup-required" } : challenge
      );
      publish();
      return { kind: "setup-required" };
    }
    if (isStepUpTooManyAttempts(error)) {
      rejectAll(reportHeadOnly(head, error));
      return { kind: "too-many-attempts" };
    }
    if (isNetworkFailure(error)) {
      rejectAll(reportHeadOnly(head, error));
      return { kind: "network" };
    }
    // Any other answer (a dual-control 409, a refusal) is this caller's to
    // handle, exactly as if the first attempt had been answered with it.
    queue = queue.filter((challenge) => challenge !== head);
    head.reject(error);
    publish();
    return afterHead();
  }
}

/** Build the header a re-send adds; everything else about the request is kept. */
export const stepUpCodeHeader = (code: string): Record<string, string> => ({
  [STEP_UP_HEADER]: code,
});

/** Test seam: drop every queued challenge and host without settling anything. */
export function resetStepUpBrokerForTests(): void {
  queue = [];
  inFlight = null;
  hosts.splice(0, hosts.length);
  listeners.clear();
}
